import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { ROLES } from '@bcc/shared';
import { AppShell } from './components/AppShell';
import { EmailConfirmRedirect } from './components/EmailConfirmRedirect';
import { RequireAuth } from './components/RequireAuth';
import { AdminLayout } from './layouts/AdminLayout';
import { Company } from './pages/Company';
import { AdminEntry } from './pages/AdminEntry';
import { Contact } from './pages/Contact';
import { CrmContactDetail } from './pages/admin/CrmContactDetail';
import { CrmContacts } from './pages/admin/CrmContacts';
import { CrmOverview } from './pages/admin/CrmOverview';
import { CrmPipeline } from './pages/admin/CrmPipeline';
import { CrmPipelineDealDetail } from './pages/admin/CrmPipelineDealDetail';
import { ActiveDealEditor } from './pages/admin/ActiveDealEditor';
import { AdminEvents } from './pages/admin/AdminEvents';
import { AdminDeals } from './pages/admin/AdminDeals';
import { AdminResources } from './pages/admin/AdminResources';
import { AdminAiApiSettings } from './pages/admin/AdminAiApiSettings';
import { AdminDealAnalyzerPrompt } from './pages/admin/AdminDealAnalyzerPrompt';
import { AdminLeads } from './pages/admin/AdminLeads';
import { AdminLeadsPipeline } from './pages/admin/AdminLeadsPipeline';
import { AdminDripCampaigns } from './pages/admin/AdminDripCampaigns';
import { CrmPipelineDeals } from './pages/admin/CrmPipelineDeals';
import { UploadDeal } from './pages/admin/UploadDeal';
import { DealDetail } from './pages/DealDetail';
import { Deals } from './pages/Deals';
import { InvestorLayout } from './layouts/InvestorLayout';
import { InvestorDashboard } from './pages/InvestorDashboard';
import { InvestorDealAnalyzer } from './pages/InvestorDealAnalyzer';
import { InvestorOfferings } from './pages/InvestorOfferings';
import { InvestorResourceArticle } from './pages/InvestorResourceArticle';
import { InvestorResources } from './pages/InvestorResources';
import { InvestorEvents } from './pages/InvestorEvents';
import { InvestorAiApiSettings } from './pages/InvestorAiApiSettings';
import { InvestorSavedAnalyses } from './pages/InvestorSavedAnalyses';
import { InvestorToolsLibrary } from './pages/tools/InvestorToolsLibrary';
import { ToolCapRate } from './pages/tools/ToolCapRate';
import { ToolCashFlow } from './pages/tools/ToolCashFlow';
import { ToolMortgage } from './pages/tools/ToolMortgage';
import { ToolQuickDeal } from './pages/tools/ToolQuickDeal';
import { ToolRentalIncome } from './pages/tools/ToolRentalIncome';
import { ToolRoiCoc } from './pages/tools/ToolRoiCoc';
import { ToolUnderwriting } from './pages/tools/ToolUnderwriting';
import { ToolValueAdd } from './pages/tools/ToolValueAdd';
import { Events } from './pages/Events';
import { Ira401k } from './pages/Ira401k';
import { Login } from './pages/Login';
import { GetStarted } from './pages/GetStarted';
import { Register } from './pages/Register';
import { StaffDashboard } from './pages/StaffDashboard';
import { Account } from './pages/Account';

const Home = lazy(() => import('./pages/Home').then((m) => ({ default: m.Home })));

export default function App() {
  return (
    <>
      <EmailConfirmRedirect />
      <Routes>
      <Route element={<AppShell />}>
        <Route
          index
          element={
            <Suspense
              fallback={
                <div className="flex min-h-[50vh] items-center justify-center bg-transparent font-mono text-sm text-[var(--home-muted-on-dark)]">
                  Loading…
                </div>
              }
            >
              <Home />
            </Suspense>
          }
        />
        <Route path="company" element={<Company />} />
        <Route path="about" element={<Navigate to="/company" replace />} />
        <Route path="ira-401k" element={<Ira401k />} />
        <Route path="contact" element={<Contact />} />
        <Route path="deals" element={<Deals />} />
        <Route path="events" element={<Events />} />
        <Route path="deals/:id" element={<DealDetail />} />
        <Route path="login" element={<Login />} />
        <Route path="getstarted" element={<GetStarted />} />
        <Route path="register" element={<Register />} />
        <Route
          path="account"
          element={
            <RequireAuth>
              <Navigate to="/dashboard/account" replace />
            </RequireAuth>
          }
        />
        <Route
          path="dashboard"
          element={
            <RequireAuth roles={[ROLES.INVESTOR, ROLES.STAFF, ROLES.ADMIN]}>
              <InvestorLayout />
            </RequireAuth>
          }
        >
          <Route index element={<InvestorDashboard />} />
          <Route path="offerings" element={<InvestorOfferings />} />
          <Route path="analyzer" element={<InvestorDealAnalyzer />} />
          <Route path="ai-api-settings" element={<InvestorAiApiSettings />} />
          <Route path="saved-analyses" element={<InvestorSavedAnalyses />} />
          <Route path="events" element={<InvestorEvents />} />
          <Route path="account" element={<Account />} />
          <Route path="resources" element={<InvestorResources />} />
          <Route path="resources/:slug" element={<InvestorResourceArticle />} />
          <Route path="tools" element={<Outlet />}>
            <Route index element={<InvestorToolsLibrary />} />
            <Route path="cash-flow" element={<ToolCashFlow />} />
            <Route path="cap-rate" element={<ToolCapRate />} />
            <Route path="rental-income" element={<ToolRentalIncome />} />
            <Route path="mortgage" element={<ToolMortgage />} />
            <Route path="value-add" element={<ToolValueAdd />} />
            <Route path="roi-coc" element={<ToolRoiCoc />} />
            <Route path="underwriting" element={<ToolUnderwriting />} />
            <Route path="quick-deal" element={<ToolQuickDeal />} />
          </Route>
        </Route>
        <Route
          path="staff"
          element={
            <RequireAuth roles={[ROLES.STAFF, ROLES.ADMIN]}>
              <StaffDashboard />
            </RequireAuth>
          }
        />

        <Route
          path="admin"
          element={
            <RequireAuth roles={[ROLES.ADMIN, ROLES.STAFF]}>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<AdminEntry />} />
          <Route path="crm" element={<CrmOverview />} />
          <Route path="crm/contacts" element={<CrmContacts />} />
          <Route path="crm/contacts/:id" element={<CrmContactDetail />} />
          <Route path="crm/pipeline" element={<CrmPipeline />} />
          <Route path="crm/deals" element={<CrmPipelineDeals />} />
          <Route path="crm/deals/:id" element={<CrmPipelineDealDetail />} />
          <Route path="upload-deal" element={<UploadDeal />} />
          <Route path="deals-catalog" element={<AdminDeals />} />
          <Route path="resources" element={<AdminResources />} />
          <Route
            path="ai-api-settings"
            element={
              <RequireAuth roles={[ROLES.ADMIN]}>
                <AdminAiApiSettings />
              </RequireAuth>
            }
          />
          <Route
            path="deal-analyzer-prompt"
            element={
              <RequireAuth roles={[ROLES.ADMIN]}>
                <AdminDealAnalyzerPrompt />
              </RequireAuth>
            }
          />
          <Route path="events" element={<AdminEvents />} />
          <Route path="leads/pipeline" element={<AdminLeadsPipeline />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="drip-campaigns" element={<AdminDripCampaigns />} />
          <Route path="active-deal" element={<ActiveDealEditor />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      </Routes>
    </>
  );
}
